import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Checkbox from "@/common/components/Checkbox";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import ResumeBox from "@/common/components/ResumeBox";
import { ShowHeader } from "@/common/components/ShowHeader";
import Skeleton from "@/common/components/Skeleton";
import useCompany from "@/common/hooks/queries/useCompany";
import { useGoBack } from "@/common/hooks/useGoBack";
import { formatCurrency } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useClientCompanyPaymentMethods } from "@/features/privateCompanies/hooks/bankingDeposits/useClientCompanyPaymentMethods";
import { useCreateBankingDeposit } from "@/features/privateCompanies/hooks/bankingDeposits/useCreateBankingDeposit";
import { bankingDepositCreateSchema, BankingDepositCreateValidationSchema } from "@/features/privateCompanies/schemas/bankingDeposits/create.schema";
import { ClientPaymentMethodData } from "@/features/privateCompanies/types/bankingDeposits/paymentMethods.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronsRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

const BankingDepositsCreate = () => {
  const { id: companyId } = useParams();

  const goBack = useGoBack();
  const navigate = useNavigate();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    data: company,
    isPending: isCompanyPending,
  } = useCompany({ id: companyId || '' });

  const {
    data: paymentMethods,
    isPending: isPaymentMethodsPending,
    dataUpdatedAt: paymentMethodsUpdatedAt,
    error: paymentMethodsError = {} as any,
  } = useClientCompanyPaymentMethods<ClientPaymentMethodData[]>({
    filters: {
      companyId,
    },
  });

  const {
    mutate: createBankingDeposit,
    isPending: isCreateBankingDepositPending,
    isError: isCreateBankingDepositError,
  } = useCreateBankingDeposit({
    companyId,
    onSuccess: (response) => {
      const bankingDepositId = response.data?.data?.id;

      if (bankingDepositId) {
        navigate(`/companies/${companyId}/banking-deposits/${bankingDepositId}`);
      } else {
        goBack({
          fallback: `/companies/${companyId}/banking-deposits`,
          searchLastRouteFromFallback: true,
        });
      }
    },
    onError: (error) => {
      setShowConfirmationModal(false);
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<BankingDepositCreateValidationSchema>({
    resolver: zodResolver(bankingDepositCreateSchema),
  });

  const onSubmit: SubmitHandler<BankingDepositCreateValidationSchema> = (data) => createBankingDeposit(data);

  useEffect(() => {
    if (!watch('payment_method') && paymentMethods && paymentMethods[0]) {
      setValue('payment_method', {
        id: paymentMethods[0].id,
        display_name: paymentMethods[0].display_name,
        admin_only: paymentMethods[0].admin_only,
      });
    }
  }, [paymentMethodsUpdatedAt]);

  return (
    <ErrorMessageWrapper statusCode={company?.kind === "public_company" ? 404 : 200}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/companies/${companyId}/banking-deposits` }}
          isLoading={isCompanyPending}
          titleClassName="font-normal text-md text-white-400"
          title={company?.general_information?.fantasy_name}
          subtitle={(
            <div className="flex items-center gap-2">
              <h2 className="text-white-400">
                Depósitos
              </h2>

              <ChevronsRightIcon className="text-white-400" />

              <h2 className="text-white-800">
                Adicionar saldo
              </h2>
            </div>
          )}
        />

        <Box className="flex flex-col gap-6 shadow-lg max-h-[calc(100vh-128px)] overflow-auto">
          <div className="flex flex-col gap-4 ">
            <p className="text-lg font-bold">
              Insira o valor
            </p>

            <div className="space-y-3">
              <Controller
                control={control}
                name="requested_total"
                render={({ field }) => (
                  <CurrencyTextField
                    label="Valor aportado"
                    markAsRequired
                    {...field}
                    errorMessage={errors.requested_total?.message || undefined}
                    disabled={isCreateBankingDepositPending}
                  />

                )}
              />

              <div className="flex items-center gap-3">
                <ResumeBox
                  padding="small"
                  boxClassName="w-full flex-1"
                  label="Crédito disponível (Pós-pago)"
                  value={company?.remaining_credit ? formatCurrency(Number(company?.remaining_credit)) : '--'}
                  isLoading={isCompanyPending}
                />

                <div className="flex-1 flex items-center gap-3">
                  <ResumeBox
                    padding="small"
                    boxClassName="w-full flex-1"
                    label="Saldo disponível (Pré-pago)"
                    value={company?.remaining_balance ? formatCurrency(Number(company?.remaining_balance)) : '--'}
                    isLoading={isCompanyPending}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <p className="text-lg font-bold">
              Forma de pagamento
            </p>

            <div className="flex items-center gap-6">
              {isPaymentMethodsPending && new Array(2).fill(null).map((_, index) => (
                <div key={`loading-payment-method-${index}`} className="flex items-center gap-3">
                  <Skeleton circle className="size-6 bg-white-100" />

                  <Skeleton width={Math.min(30 * (index + 1), 80)} />
                </div>
              ))}

              {!isPaymentMethodsPending && paymentMethods.map((paymentMethod, index) => (
                <Checkbox
                  name="payment_method"
                  type="radio"
                  className="items-center gap-3"
                  boxClassName="rounded-full size-6"
                  defaultChecked={index === 0}
                  disabled={isCreateBankingDepositPending}
                  labelClassName="text-base"
                  label={paymentMethod.display_name}
                  dataTestidPrefix={`payment_method_${paymentMethod.display_name.toLowerCase()}`}
                  Icon={paymentMethod.icon?.url ? (
                    <img
                      src={paymentMethod.icon?.url}
                      alt={paymentMethod.display_name}
                      className="size-7"
                    />
                  ) : undefined}
                  onChange={() => setValue("payment_method", {
                    id: paymentMethod.id,
                    display_name: paymentMethod.display_name,
                    admin_only: !!paymentMethod.admin_only,
                  })}
                />
              ))}

              {!isPaymentMethodsPending && Number(paymentMethods.length) === 0 && (
                <p className="italic text-sm text-white-400" data-testid="empty_payment_methods_message">
                  {paymentMethodsError?.status === 403
                    ? 'Você não tem permissão para listar formas de pagamentos.'
                    : 'Sua empresa não possui formas de pagamentos cadastradas.'}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              name="submit"
              variant="success"
              type="button"
              onClick={handleSubmit(() => setShowConfirmationModal(true))}
              disabled={isCreateBankingDepositPending || !watch('payment_method')}
              tooltipMessage={!watch('payment_method')
                ? 'Não é possível fazer um aporte sem selecionar uma Forma de pagamento'
                : undefined}
              operationKlassToCheckPermission="Admin::Banking::Deposits::Create"
            >
              Avançar para pagamento
            </Button>
          </div>
        </Box>

        <Modal
          isOpen={showConfirmationModal}
          setIsOpen={setShowConfirmationModal}
          title="Você tem certeza?"
          subtitle={
            watch('payment_method')?.admin_only
              ? 'O Saldo do cliente será aportado imediatamente e não será possível editar o saldo.'
              : 'Seu pix será gerado agora, caso deseje outro valor, faça um novo depósito.'
          }
          titleClass="text-2xl"
          subtitleClassName="mt-2 text-white-400"
          useCancellableFooter
          showClose={false}
          confirmButtonText="Confirmar Depósito"
          confirmButtonClassName="bg-primary-blue"
          confirmButtonIsLoading={isCreateBankingDepositPending}
          onConfirm={handleSubmit(onSubmit)}
          disabled={isCreateBankingDepositPending}
          dataTestid="confirm_submit_button"
        />

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError={isCreateBankingDepositError}
            dataTestid="create_banking_deposit_modal_message"
          />
        )}
      </form>
    </ErrorMessageWrapper>
  );
}

export default BankingDepositsCreate;
